import { useState } from 'react';
import { Calculator, Copy } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { t } from '@lingui/macro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { TariffRate, TariffVersion } from '@/types/billing';
import type { AdjustmentConfig } from './TariffBulkAdjustmentForm';
import { TariffBulkSelectionPanel, getDayNumbers, SERVICE_OPTIONS } from './TariffBulkSelectionPanel';
import { useCurrency } from '@/hooks/useCurrency';
import { DEFAULT_CURRENCY } from '@/lib/money';

interface TariffBulkActionsProps {
  versionId: string;
  currentRates: TariffRate[];
  previousVersions?: TariffVersion[];
  onApplyAdjustment?: (adjustment: AdjustmentConfig) => void;
  onCopyRates?: (sourceService: string, targetService: string) => void;
  onImportFromVersion?: (versionId: string) => void;
  onUpdate?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

/**
 * Orchestrator component for bulk tariff actions.
 * Manages state and delegates to specialized child components.
 */
export function TariffBulkActions({
  versionId: _versionId,
  currentRates: _currentRates,
  previousVersions = [],
  onApplyAdjustment = () => {},
  onCopyRates = () => {},
  onImportFromVersion = () => {},
  onUpdate,
  isLoading = false,
  isDisabled = false,
}: TariffBulkActionsProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const activeCurrency = currency || DEFAULT_CURRENCY;
  
  // Adjustment form state
  const [adjustmentValue, setAdjustmentValue] = useState<number>(10);
  const [selectedService, setSelectedService] = useState<string>('ALL');
  const [selectedDays, setSelectedDays] = useState<string>('ALL');
  
  // Copy rates state
  const [copySource, setCopySource] = useState<string>('');
  const [copyTarget, setCopyTarget] = useState<string>('');
  
  // Import version state
  const [selectedVersion, setSelectedVersion] = useState<string>('');

  const handlePercentageAdjustment = (operation: 'increase' | 'decrease') => {
    const services = selectedService === 'ALL' ? undefined : [selectedService];
    const days = getDayNumbers(selectedDays);

    onApplyAdjustment({
      type: 'percentage',
      operation,
      value: adjustmentValue,
      services,
      days,
    });
    onUpdate?.();
  };

  const handleFixedAdjustment = (operation: 'increase' | 'decrease') => {
    const services = selectedService === 'ALL' ? undefined : [selectedService];
    const days = getDayNumbers(selectedDays);

    onApplyAdjustment({
      type: 'fixed',
      operation,
      value: adjustmentValue,
      services,
      days,
    });
    onUpdate?.();
  };

  const handleCopyRates = () => {
    if (copySource && copyTarget && copySource !== copyTarget) {
      onCopyRates(copySource, copyTarget);
      onUpdate?.();
    }
  };

  const handleImport = () => {
    if (selectedVersion) {
      onImportFromVersion(selectedVersion);
      onUpdate?.();
    }
  };

  return (
    <Accordion type="single" collapsible className="w-full space-y-4">
      {/* Quick Adjustments */}
      <AccordionItem value="adjustments" className="border rounded-lg px-3">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            {i18n._(t`Quick Adjustments`)}
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-3 pt-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="adjustment-value">{i18n._(t`Value`)}</Label>
              <Input
                id="adjustment-value"
                type="number"
                value={adjustmentValue}
                onChange={(e) => setAdjustmentValue(parseFloat(e.target.value) || 0)}
                placeholder="10"
                disabled={isLoading || isDisabled}
                aria-label={i18n._(t`Adjustment value`)}
              />
            </div>
            <TariffBulkSelectionPanel
              selectedService={selectedService}
              selectedDays={selectedDays}
              onServiceChange={setSelectedService}
              onDaysChange={setSelectedDays}
              isDisabled={isLoading || isDisabled}
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePercentageAdjustment('increase')}
              disabled={isLoading || isDisabled}
              aria-label={i18n._(t`Increase by percentage`)}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              +{adjustmentValue}%
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePercentageAdjustment('decrease')}
              disabled={isLoading || isDisabled}
              aria-label={i18n._(t`Decrease by percentage`)}
            >
              <TrendingDown className="w-4 h-4 mr-1" />
              -{adjustmentValue}%
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFixedAdjustment('increase')}
              disabled={isLoading || isDisabled}
              aria-label={i18n._(t`Increase by fixed amount`)}
            >
              +{activeCurrency} {adjustmentValue}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFixedAdjustment('decrease')}
              disabled={isLoading || isDisabled}
              aria-label={i18n._(t`Decrease by fixed amount`)}
            >
              -{activeCurrency} {adjustmentValue}
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Copy Rates Between Services */}
      <AccordionItem value="copy" className="border rounded-lg px-3">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-2">
            <Copy className="w-4 h-4" />
            {i18n._(t`Copy Rates Between Services`)}
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="copy-source">{i18n._(t`From Service`)}</Label>
              <Select
                value={copySource}
                onValueChange={setCopySource}
                disabled={isLoading || isDisabled}
              >
                <SelectTrigger id="copy-source" aria-label={i18n._(t`Select source service`)}>
                  <SelectValue placeholder={i18n._(t`Select source`)} />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.slice(1).map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="copy-target">{i18n._(t`To Service`)}</Label>
              <Select
                value={copyTarget}
                onValueChange={setCopyTarget}
                disabled={isLoading || isDisabled}
              >
                <SelectTrigger id="copy-target" aria-label={i18n._(t`Select target service`)}>
                  <SelectValue placeholder={i18n._(t`Select target`)} />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.slice(1).map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleCopyRates}
            disabled={isLoading || isDisabled || !copySource || !copyTarget || copySource === copyTarget}
            className="w-full"
            aria-label={i18n._(t`Copy rates from source to target service`)}
          >
            <Copy className="w-4 h-4 mr-2" />
            {i18n._(t`Copy Rates`)}
          </Button>
        </AccordionContent>
      </AccordionItem>

      {/* Import from Previous Version */}
      {previousVersions.length > 0 && (
        <AccordionItem value="import" className="border rounded-lg px-3">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Copy className="w-4 h-4" />
              {i18n._(t`Import from Previous Version`)}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <Select
              value={selectedVersion}
              onValueChange={setSelectedVersion}
              disabled={isLoading || isDisabled}
            >
              <SelectTrigger aria-label={i18n._(t`Select version to import from`)}>
                <SelectValue placeholder={i18n._(t`Select version`)} />
              </SelectTrigger>
              <SelectContent>
                {previousVersions.map(version => (
                  <SelectItem key={version.id} value={version.id || ''}>
                    {version.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleImport}
              disabled={isLoading || isDisabled || !selectedVersion}
              className="w-full"
              aria-label={i18n._(t`Import rates from selected version`)}
            >
              {i18n._(t`Import Rates`)}
            </Button>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
}

// Re-export types and components for convenience
export type { AdjustmentConfig } from './TariffBulkAdjustmentForm';
